/**
 * Mapa rota UI do **painel do cliente** → artigo do centro de ajuda (DOC 17).
 * Paths sem artigo → home do help (não forçar domain-setup).
 *
 * Proibido: entradas `/super-admin/*` — ajuda pública é só cliente ([17] § 9).
 */
export const helpRouteMap: Record<string, string> = {
  "/settings/sending": "/pt-BR/sending/domain-setup",
  "/settings/sending-controls": "/pt-BR/sending/sending-controls",
  "/settings/deliverability": "/pt-BR/sending/deliverability",
  "/settings/subscription": "/pt-BR/billing/quota-and-plans",
  "/suspended": "/pt-BR/billing/quota-and-plans",
  "/settings/account": "/pt-BR/getting-started/account-settings",
  "/login/mfa": "/pt-BR/getting-started/account-settings",
  "/settings/team": "/pt-BR/getting-started/team",
  "/settings/workspace": "/pt-BR/getting-started/workspace-preferences",
  "/settings/referral": "/pt-BR/getting-started/referrals",
  "/settings/contact-fields": "/pt-BR/contacts/contact-fields",
  "/settings/api-tokens": "/pt-BR/getting-started/api-tokens",
  "/settings/webhooks": "/pt-BR/getting-started/webhooks",
  "/integrations": "/pt-BR/integrations/hub",
  "/integrations/vtex": "/pt-BR/integrations/vtex",
  "/integrations/shopify": "/pt-BR/integrations/shopify",
  "/integrations/uappi": "/pt-BR/integrations/uappi",
  "/integrations/site-pixel": "/pt-BR/integrations/site-pixel",
  "/settings/brand-kit": "/pt-BR/getting-started/brand-kit",
  "/getting-started/starter": "/pt-BR/getting-started/starter-sem-loja",
  "/templates/gallery": "/pt-BR/content/ready-made-templates",
  "/templates": "/pt-BR/content/ready-made-templates",
  "/register": "/pt-BR/getting-started/create-account",
  "/contacts": "/pt-BR/contacts/manage",
  "/contacts/blocklist": "/pt-BR/contacts/block-list",
  "/contacts/inactive": "/pt-BR/contacts/inactive-contacts",
  "/contacts/import": "/pt-BR/contacts/import-csv",
  "/tags": "/pt-BR/contacts/tags",
  "/segments": "/pt-BR/contacts/segments",
  "/forms": "/pt-BR/automations/forms",
  "/rss-feeds": "/pt-BR/automations/rss-to-email",
  "/automations/welcome": "/pt-BR/automations/welcome",
  "/automations/abandoned-cart": "/pt-BR/automations/abandoned-cart",
  "/automations/win-back": "/pt-BR/automations/win-back",
  "/automations/nurture-curso": "/pt-BR/automations/nurture-curso",
  "/automations/saas-lifecycle": "/pt-BR/automations/saas-lifecycle",
  "/campaigns/new": "/pt-BR/campaigns/create",
  "/campaigns": "/pt-BR/getting-started/first-campaign",
  "/sends": "/pt-BR/sends/metrics",
  "/dashboard": "/pt-BR/contacts/high-bounce",
};

export const HELP_BASE_URL =
  process.env.REACT_APP_HELP_URL?.replace(/\/$/, "") ||
  "https://ajuda.brenvio.com.br";

/** Normaliza locale do i18n para prefixo do VitePress. */
function helpLocalePrefix(locale: string): string {
  const l = (locale || "pt-BR").toLowerCase();
  if (l.startsWith("en")) return "en";
  if (l.startsWith("es")) return "es";
  return "pt-BR";
}

/**
 * URL direta de um artigo VitePress (ex. `/billing/coupons`).
 * Use quando a tela precisa de um guia específico, não o mapa da rota.
 */
export function helpArticleUrl(articlePath: string, locale = "pt-BR"): string {
  const loc = helpLocalePrefix(locale);
  let path = (articlePath || "").trim();
  if (!path.startsWith("/")) {
    path = `/${path}`;
  }
  if (/^\/(pt-BR|en|es)\b/.test(path)) {
    path = path.replace(/^\/(pt-BR|en|es)\b/, `/${loc}`);
  } else {
    path = `/${loc}${path}`;
  }
  return HELP_BASE_URL + path;
}

/**
 * Resolve artigo pelo path (match exato ou prefixo mais longo).
 * Sem match → home do help no locale atual.
 */
export function helpUrlForRoute(routePath: string, locale = "pt-BR"): string {
  const path = (routePath || "/").split("?")[0].replace(/\/$/, "") || "/";
  const loc = helpLocalePrefix(locale);

  let slug: string | undefined = helpRouteMap[path];
  if (!slug) {
    const prefixes = Object.keys(helpRouteMap).sort(
      (a, b) => b.length - a.length
    );
    const hit = prefixes.find(
      (p) => path === p || path.startsWith(`${p}/`)
    );
    if (hit) slug = helpRouteMap[hit];
  }

  if (!slug) {
    return `${HELP_BASE_URL}/${loc}`;
  }

  // Troca locale do slug mapeado (hoje stubs em pt-BR) pelo idioma da UI.
  const localized = slug.replace(/^\/(pt-BR|en|es)\b/, `/${loc}`);

  if (
    localized.startsWith("/pt-BR/") ||
    localized.startsWith("/en/") ||
    localized.startsWith("/es/")
  ) {
    return HELP_BASE_URL + localized;
  }
  return `${HELP_BASE_URL}/${loc}${localized.startsWith("/") ? localized : `/${localized}`}`;
}
